import React, { useState, useRef, useEffect, useMemo } from "react";
import clsx from "clsx";
import moment from "moment";
import toast from "react-hot-toast";
import { SpaceMessage } from "types/spaceTypes";
import SpaceMessageAvatar from "./SpaceMessageAvatar";
import SpaceMessageAttachments from "./SpaceMessageAttachments";
import CustomToast from "components/common/CustomToast";
import { parseMentions, extractMentions, SpaceMember, SpaceGuest } from "utils/mentionUtils";
import SpaceAgentThinkingState from "./SpaceMessageAgentThinkingState";
import SpaceAIResponseRenderer from "./SpaceMessageAIResponseRenderer";
import SpaceToolRenderer from "./SpaceMessageToolRenderer";

type SpaceStyleMessageProps = {
    message: SpaceMessage;
    showHeader?: boolean; // Show avatar, name, timestamp
    isGrouped?: boolean; // Part of a message group
    onHover?: (messageId: string) => void;
    onPinMessage?: (messageId: string) => void;
    onUnpinMessage?: (messageId: string) => void;
    onEditMessage?: (messageId: string, newContent: string) => void;
    onDeleteMessage?: (messageId: string) => void;
    onMarkAsRead?: (messageId: string) => void;
    readMessagesRef?: React.RefObject<Set<string>>;
    currentUserId?: string;
    spaceMembers?: (SpaceMember | SpaceGuest)[];
    onOpenCanvas?: (message: any) => void;
};

export default function SpaceStyleMessage({
    message,
    showHeader = true,
    isGrouped = false,
    onHover,
    onPinMessage,
    onUnpinMessage,
    onEditMessage,
    onDeleteMessage,
    onMarkAsRead,
    readMessagesRef,
    currentUserId = "elizabeth-fisher",
    spaceMembers = [],
    onOpenCanvas
}: SpaceStyleMessageProps) {
    // State for expanding tool components (like in Flow)
    const [expandedId, setExpandedId] = useState<string>("");
    const [showDropdown, setShowDropdown] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const [isHovered, setIsHovered] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [editContent, setEditContent] = useState(message.content);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const dropdownButtonRef = useRef<HTMLButtonElement>(null);
    const editTextareaRef = useRef<HTMLTextAreaElement>(null);
    
    // Check if this message is marked as read
    const isMessageRead = readMessagesRef?.current?.has(message.id) ?? false;
    
    // Calculate dynamic width based on content length
    const getEditingWidth = () => {
        const contentLength = editContent.length;
        const lineCount = editContent.split('\n').length;
        const longestLine = Math.max(...editContent.split('\n').map(line => line.length));
        
        // Base width on longest line or content length, whichever gives better UX
        if (longestLine < 50 && lineCount === 1) {
            // Short single line - use fit content with min width
            return "w-fit min-w-[300px]";
        } else if (longestLine < 100 && contentLength < 200) {
            // Medium content - moderate width
            return "w-fit min-w-[400px] max-w-lg";
        } else {
            // Long content - full width with reasonable max
            return "w-full max-w-2xl";
        }
    };
    
    // Check if this is an AI agent message
    const isAIAgent = message.author.id === "synth" || message.author.name === "Synth";
    
    // Check if current user owns this message
    // Handle both brand members (ldbId) and guest users (guestId)
    const isOwnMessage = useMemo(() => {
        if (!currentUserId || currentUserId === "unknown-user") {
            return false;
        }
        
        // Direct ID match
        if (message.author.id === currentUserId) {
            return true;
        }
        
        return false;
    }, [message.author.id, currentUserId]);
    
    
    // Check if message contains @Synth mention (should not be editable)
    const containsSynthMention = extractMentions(message.content).some(
        mention => mention.toLowerCase().trim().startsWith('synth')
    );
    
    // Check if this message has tool content or is an AI message that needs more width
    const hasToolContent = message.toolName || message.resultMessage || message.type === 'ai';

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        }

        if (showDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDropdown]);

    const handlePin = () => {
        onPinMessage?.(message.id);
        setShowDropdown(false);
    };

    const handleUnpin = () => {
        onUnpinMessage?.(message.id);
        setShowDropdown(false);
    };

    const handleEditStart = () => {
        setIsEditing(true);
        setEditContent(message.content);
        setShowDropdown(false);
        // Focus the textarea after it renders
        setTimeout(() => {
            editTextareaRef.current?.focus();
        }, 0);
    };

    const handleEditSave = () => {
        if (editContent.trim() && editContent.trim() !== message.content) {
            onEditMessage?.(message.id, editContent.trim());
        }
        setIsEditing(false);
    };

    const handleEditCancel = () => {
        setEditContent(message.content);
        setIsEditing(false);
    };

    const handleEditKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleEditSave();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            handleEditCancel();
        }
    };

    const handleDeleteStart = () => {
        setShowDeleteModal(true);
        setShowDropdown(false);
    };

    const handleDeleteConfirm = () => {
        onDeleteMessage?.(message.id);
        setShowDeleteModal(false);
    };

    const handleDeleteCancel = () => {
        setShowDeleteModal(false);
    };

    const handleMarkAsRead = () => {
        onMarkAsRead?.(message.id);
        setShowDropdown(false);
    };

    const handleCopyText = async () => {
        try {
            // Copy message content to clipboard
            await navigator.clipboard.writeText(message.content);
            
            // Show success toast
            toast.custom((t) => (
                <CustomToast
                    t={t}
                    message="Message text copied to clipboard"
                    type="success"
                />
            ));
            
            // Close dropdown
            setShowDropdown(false);
        } catch (error) {
            console.error('Failed to copy message text:', error);
            
            // Fallback for older browsers
            try {
                const textArea = document.createElement('textarea');
                textArea.value = message.content;
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
                
                toast.custom((t) => (
                    <CustomToast
                        t={t}
                        message="Message text copied to clipboard"
                        type="success"
                    />
                ));
            } catch (fallbackError) {
                toast.custom((t) => (
                    <CustomToast
                        t={t}
                        message="Failed to copy message text"
                        type="error"
                    />
                ));
            }
            
            setShowDropdown(false);
        }
    };

    const handleCopyLink = async () => {
        try {
            // Get current URL and construct message permalink
            const baseUrl = window.location.origin;
            const spaceId = window.location.pathname.split('/')[2]; // Extract spaceId from current path
            const messageLink = `${baseUrl}/spaces/${spaceId}/messages/${message.id}`;
            
            // Copy to clipboard
            await navigator.clipboard.writeText(messageLink);
            
            // Show success toast
            toast.custom((t) => (
                <CustomToast
                    t={t}
                    message="Message link copied to clipboard"
                    type="success"
                />
            ));
            
            // Close dropdown
            setShowDropdown(false);
        } catch (error) {
            console.error('Failed to copy message link:', error);
            
            // Fallback for older browsers
            try {
                const textArea = document.createElement('textarea');
                const baseUrl = window.location.origin;
                const spaceId = window.location.pathname.split('/')[2];
                const messageLink = `${baseUrl}/spaces/${spaceId}/messages/${message.id}`;
                textArea.value = messageLink;
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
                
                toast.custom((t) => (
                    <CustomToast
                        t={t}
                        message="Message link copied to clipboard"
                        type="success"
                    />
                ));
            } catch (fallbackError) {
                toast.custom((t) => (
                    <CustomToast
                        t={t}
                        message="Failed to copy message link"
                        type="error"
                    />
                ));
            }
            
            setShowDropdown(false);
        }
    };

    return (
        <div 
            className={clsx(
                isGrouped ? "pt-2" : showHeader ? "pt-8" : "pt-6"
            )}
            data-message-id={message.id}
        >
            <div className="flex gap-2 items-start">
                {/* Avatar column */}
                <div className="w-9 flex-shrink-0 flex justify-start">
                    {showHeader && (
                        <SpaceMessageAvatar 
                            author={message.author} 
                            size="md" 
                        />
                    )}
                </div>
                
                {/* Message Card */}
                <div 
                    className={clsx(
                        "relative flex items-start",
                        isEditing ? "w-full max-w-none" : (
                            hasToolContent ? "w-full max-w-[90%]" : "max-w-[75%]"
                        )
                    )}
                    onMouseEnter={() => {
                        setIsHovered(true);
                        onHover?.(message.id);
                    }}
                    onMouseLeave={(e) => {
                        // Don't hide if dropdown is open or if moving to the dropdown/button
                        const relatedTarget = e.relatedTarget as HTMLElement;
                        if (!showDropdown && !(relatedTarget && typeof relatedTarget.closest === 'function' && relatedTarget.closest('[data-dropdown-area]'))) {
                            setIsHovered(false);
                        }
                    }}
                    data-dropdown-area
                >
                    <div 
                        className={clsx(
                            "group bg-gray-50 hover:bg-gray-100 transition-colors rounded-md px-2 py-1.5 border border-gray-100 relative leading-tight inline-block",
                            isEditing ? getEditingWidth() : (message.type === 'ai' ? "w-full" : "max-w-fit")
                        )}
                        style={{ minWidth: 'fit-content' }}
                    >
                    {/* Header: Name and timestamp or Editing message - Hidden for AI agents */}
                    {showHeader && !isAIAgent && (
                        <div className="my-1 p-0 leading-tight text-sm">
                            <span className={clsx(
                                "font-medium m-0 p-0 leading-tight",
                                message.author.role === "guest" ? "text-[#6562E5]" : "text-[#042821]"
                            )}>
                                {isEditing ? 'Editing message' : message.author.name}
                            </span>
                                {!isEditing && (
                                    <span className="text-xs text-gray-500 ml-3 p-0 leading-tight">
                                        {message.editedAt ? 'Sent at ' : ''}{moment(message.createdAt).format('h:mm A')}
                                    </span>
                                )}
                        </div>
                    )}

                    {/* Timestamp for AI agents - shown separately */}
                    {showHeader && isAIAgent && !isEditing && (
                        <div className="my-1 p-0 leading-tight text-sm">
                            <span className="text-xs text-gray-500 p-0 leading-tight">
                                {moment(message.createdAt).format('h:mm A')}
                            </span>
                        </div>
                    )}
                    
                        {/* Message text or editing interface */}
                        {isEditing ? (
                            <div className="w-full">
                                <textarea
                                    ref={editTextareaRef}
                                    value={editContent}
                                    onChange={(e) => setEditContent(e.target.value)}
                                    onKeyDown={handleEditKeyDown}
                                    className="w-full min-h-[120px] border-0 resize-none focus:outline-none text-sm bg-transparent mt-2 py-2 m-0 px-0 leading-snug"
                                />
                                <div className="flex justify-end gap-2 mt-4">
                                    <button
                                        onClick={handleEditCancel}
                                        className="px-4 py-2 text-sm rounded-md transition-colors bg-gray-200 text-gray-700 hover:bg-gray-300"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleEditSave}
                                        disabled={!editContent.trim() || editContent.trim() === message.content}
                                        className="px-6 py-2 bg-y-10 text-black text-sm rounded-md hover:opacity-90 disabled:bg-y-10 disabled:opacity-60 disabled:cursor-not-allowed transition-opacity font-medium"
                                    >
                                        Save
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div>
                                {/* Show pinned indicator if message is pinned by current user */}
                                {message.isPinned && (
                                    <div className="flex items-center gap-1 mb-1 text-xs text-blue-600">
                                        <i className="ri-pushpin-2-fill transform rotate-45"></i>
                                        <span className="font-medium">
                                            {message.pinnedBy === currentUserId ? 'Pinned by you' : `Pinned by ${message.author.name}`}
                                        </span>
                                    </div>
                                )}

                 <div 
                     className="text-gray-900 text-sm m-0 p-0 leading-snug"
                 >
                     {message.content === "THINKING_INDICATOR" || message.content === "Processing your request..." ? (
                         <SpaceAgentThinkingState isLoading={true} />
                     ) : message.type === "ai" ? (
                         <div className="overflow-x-scroll">
                             {/* Render tool UI if this message has tool calls */}
                             <SpaceToolRenderer
                                 message={message}
                                 expandedId={expandedId}
                                 setExpandedId={setExpandedId}
                             />
                             {/* Render text content */}
                             <SpaceAIResponseRenderer 
                                 content={message.content} 
                                 messageType={message.messageType}
                                 createdAt={message.createdAt}
                             />
                         </div>
                     ) : (
                         parseMentions(message.content, spaceMembers)
                     )}
                 </div>
                 
                 {/* Show attachments if they exist */}
                 {message.attachments && message.attachments.length > 0 && (
                     <SpaceMessageAttachments 
                         attachments={message.attachments} 
                     />
                 )}
                 
                                {/* Show edited indicator if message was edited */}
                                {message.editedAt && (
                                    <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
                                        <i className="ri-edit-line"></i>
                                        <span>
                                            Edited • {moment(message.editedAt).calendar(null, {
                                                sameDay: '[Today at] h:mm A',
                                                lastDay: '[Yesterday at] h:mm A',
                                                lastWeek: 'dddd [at] h:mm A',
                                                sameElse: 'MMM D, YYYY [at] h:mm A'
                                            })}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Three dots button*/}
                    {/* Hide actions menu for AI messages */}
                    {!isAIAgent && (isHovered || showDropdown) && (
                        <div className="relative ml-2 flex-shrink-0 self-start">
                            <button
                                ref={dropdownButtonRef}
                                className="p-1 rounded hover:bg-gray-200 transition-colors"
                                onClick={(e) => {
                                    if (!showDropdown && dropdownButtonRef.current) {
                                        const rect = dropdownButtonRef.current.getBoundingClientRect();
                                        setDropdownPosition({
                                            top: rect.bottom + 5,
                                            left: rect.left + 10
                                        });
                                    }
                                    setShowDropdown(!showDropdown);
                                }}
                                data-dropdown-area
                            >
                                <i className="ri-more-2-line text-gray-600 text-sm"></i>
                            </button>

                            {/* Dropdown menu*/}
                            {showDropdown && (
                                <div 
                                    ref={dropdownRef}
                                    className="fixed bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-[9999] w-[270px]"
                                    style={{
                                        top: `${dropdownPosition.top}px`,
                                        left: `${dropdownPosition.left}px`
                                    }}
                                    data-dropdown-area
                                >
                            {/* Hidden: Mark as read/unread button
                            <button 
                                onClick={handleMarkAsRead}
                                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                                {isMessageRead ? "Mark as unread" : "Mark as read"}
                            </button>
                            */}
                            {/* Show pin/unpin option based on current state */}
                            {message.isPinned ? (
                                <button 
                                    onClick={handleUnpin}
                                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                    Unpin from this space
                                </button>
                            ) : (
                                <button 
                                    onClick={handlePin}
                                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                    Pin to this space
                                </button>
                            )}
                            <button 
                                onClick={handleCopyText}
                                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                                Copy text
                            </button>
                            <button 
                                onClick={handleCopyLink}
                                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                                Copy link to this chat
                            </button>
                            {/* Only show edit/delete options for own messages (not AI and no @Synth mentions) */}
                            {isOwnMessage && !isAIAgent && !containsSynthMention && (
                                <>
                                    <button 
                                        onClick={handleEditStart}
                                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                        Edit message
                                    </button>
                                    <button 
                                        onClick={handleDeleteStart}
                                        className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-gray-50 transition-colors"
                                    >
                                        Delete message
                                    </button>
                                </>
                            )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Delete confirmation modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-[500px] w-full">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-medium text-gray-900">
                                    Delete message
                                </h3>
                                <button
                                    onClick={handleDeleteCancel}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <i className="ri-close-line text-xl"></i>
                                </button>
                            </div>
                            
                            <p className="text-sm text-gray-600 mb-6">
                                Are you sure you want to delete this message? Deleted messages cannot be recovered.
                            </p>
                            
                            {/* Message preview */}
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                                <div className="flex items-start gap-3">
                                    <SpaceMessageAvatar 
                                        author={message.author} 
                                        size="sm" 
                                    />
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-sm font-medium text-gray-900">
                                                {message.author.name}
                                            </span>
                                            <span className="text-xs text-gray-500">
                                                {moment(message.createdAt).format('h:mm A')}
                                            </span>
                                        </div>
                                     <div className="text-sm text-gray-700 line-clamp-3">
                                         {message.type === "ai" ? (
                                             <SpaceAIResponseRenderer content={message.content} messageType={message.messageType} />
                                         ) : (
                                             parseMentions(message.content, spaceMembers)
                                         )}
                                     </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={handleDeleteCancel}
                                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                                >
                                    No, cancel
                                </button>
                                <button
                                    onClick={handleDeleteConfirm}
                                    className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition-colors"
                                >
                                    Delete message
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
